import {
  type LanguageCode,
  withLanguagePath,
} from '@/lib/url-language-utils'

export const ARENA_SEO_LANGUAGES: ReadonlyArray<[LanguageCode, string]> = [
  ['en', 'en'],
  ['zh', 'zh-Hans'],
  ['tw', 'zh-Hant'],
  ['ja', 'ja'],
  ['ko', 'ko'],
  ['ar', 'ar'],
  ['es', 'es'],
  ['fr', 'fr'],
]

export function arenaLanguageTag(language: string): string {
  return ARENA_SEO_LANGUAGES.find(([code]) => code === language)?.[1] ?? 'en'
}

export function arenaSeoOrigin(): string {
  if (typeof window === 'undefined')
    return 'https://werewolf.mentiss.ai'
  if (window.location.hostname === 'b-werewolf.mentiss.ai')
    return 'https://b-werewolf.mentiss.ai'
  return window.location.hostname === 'werewolf.mentiss.ai'
    ? 'https://werewolf.mentiss.ai'
    : window.location.origin
}

export function arenaSeoRobots(indexable: boolean): string {
  return typeof window !== 'undefined'
    && window.location.hostname === 'werewolf.mentiss.ai'
    ? indexable ? 'index, follow' : 'noindex, follow'
    : 'noindex, nofollow'
}

export function arenaDirectoryPath(language: LanguageCode, page = 1): string {
  const path = withLanguagePath('/games', language)
  return page > 1 ? `${path}?page=${page}` : path
}

export interface ArenaDynamicHead {
  title: string
  socialTitle?: string
  description: string
  language: string
  robots: string
  canonical?: string
  alternates?: ReadonlyArray<{ hreflang: string, href: string }>
  previous?: string
  next?: string
  structuredData?: Record<string, unknown>
  structuredDataId?: 'seo-game-directory-structured-data' | 'seo-forum-structured-data'
}

export function applyArenaDynamicHead(head: ArenaDynamicHead): () => void {
  const owned: Element[] = []
  const remove = (selector: string) =>
    document.head.querySelectorAll(selector).forEach(element => element.remove())
  const meta = (attribute: 'name' | 'property', key: string, content: string) => {
    remove(`meta[${attribute}="${key}"]`)
    const element = document.createElement('meta')
    element.setAttribute(attribute, key)
    element.content = content
    document.head.append(element)
    owned.push(element)
  }
  const link = (rel: string, href: string, hreflang?: string) => {
    const element = document.createElement('link')
    element.rel = rel
    element.href = href
    if (hreflang)
      element.hreflang = hreflang
    document.head.append(element)
    owned.push(element)
  }

  document.title = head.title
  document.documentElement.lang = head.language
  document.documentElement.dir = head.language === 'ar' ? 'rtl' : 'ltr'
  meta('name', 'description', head.description)
  meta('name', 'robots', head.robots)
  meta('property', 'og:type', 'website')
  meta('property', 'og:title', head.socialTitle ?? head.title)
  meta('property', 'og:description', head.description)
  meta('property', 'og:image', 'https://mentiss.ai/asset/logo.jpg')
  if (head.canonical)
    meta('property', 'og:url', head.canonical)
  else
    remove('meta[property="og:url"]')
  meta('name', 'twitter:card', 'summary_large_image')
  meta('name', 'twitter:title', head.socialTitle ?? head.title)
  meta('name', 'twitter:description', head.description)
  remove('meta[name="twitter:image"]')
  if (head.canonical)
    meta('name', 'twitter:url', head.canonical)
  else
    remove('meta[name="twitter:url"]')

  remove('link[rel="canonical"], link[rel="alternate"][hreflang], link[rel="prev"], link[rel="next"]')
  if (head.canonical)
    link('canonical', head.canonical)
  for (const alternate of head.alternates ?? [])
    link('alternate', alternate.href, alternate.hreflang)
  if (head.previous)
    link('prev', head.previous)
  if (head.next)
    link('next', head.next)

  remove('#site-default-structured-data, #arena-structured-data, #seo-game-directory-structured-data, #seo-game-log-structured-data, #seo-forum-structured-data')
  if (head.structuredData && head.structuredDataId) {
    const script = document.createElement('script')
    script.id = head.structuredDataId
    script.type = 'application/ld+json'
    script.textContent = JSON.stringify(head.structuredData)
    document.head.append(script)
    owned.push(script)
  }

  return () => {
    owned.forEach(element => element.remove())
    document.title = 'Mentiss Arena'
    document.documentElement.lang = 'en'
    document.documentElement.dir = 'ltr'
    remove('meta[name="robots"]')
    const robots = document.createElement('meta')
    robots.name = 'robots'
    const destination = window.location.pathname.replace(/^\/(?:en|zh-hans|zh-hant|ja|ko|ar|es|fr)(?=\/|$)/, '') || '/'
    robots.content = arenaSeoRobots(
      destination === '/' || destination === '/play/rule' || destination === '/play/roles',
    )
    document.head.append(robots)
  }
}
